import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getProfileVerticalFromAppContext } from '@photofeeler/shared';
import { useGameStore } from '@/stores/game';
import { getLessonsByContext } from '@/data/lessons';
import { CONTEXTS } from '@/data/contexts';
import { showToast } from '@/components/Toast';

export function LearnTab() {
  const navigate = useNavigate();
  const {
    activeContexts,
    primaryContext,
    completedLessons,
    streakDays,
    lastStreakDate,
    completeLesson,
    updateStreak,
  } = useGameStore();

  const [selectedContext, setSelectedContext] = useState(primaryContext || 'dating');
  const [expandedLesson, setExpandedLesson] = useState<string | null>(null);

  const lessons = getLessonsByContext(selectedContext);

  const displayContexts = activeContexts.length > 0
    ? CONTEXTS.filter(c => activeContexts.includes(c.id))
    : CONTEXTS.slice(0, 1);

  // Find first incomplete lesson
  const firstIncompleteLessonId = useMemo(() => {
    for (const lesson of lessons) {
      if (!completedLessons.includes(lesson.id)) return lesson.id;
    }
    return null;
  }, [lessons, completedLessons]);

  // Auto-expand first incomplete
  const effectiveExpanded = expandedLesson ?? firstIncompleteLessonId;

  // Streak calendar - last 14 days
  const streakDots = useMemo(() => {
    const dots: { date: string; active: boolean }[] = [];
    const today = new Date();
    for (let i = 13; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      // Simplified: show as active if within streak range ending at lastStreakDate
      const isActive = lastStreakDate != null && streakDays > 0 && (() => {
        const last = new Date(lastStreakDate);
        const diff = Math.floor((last.getTime() - d.getTime()) / (24 * 60 * 60 * 1000));
        return diff >= 0 && diff < streakDays;
      })();
      dots.push({ date: iso, active: isActive });
    }
    return dots;
  }, [streakDays, lastStreakDate]);

  const handleComplete = (lessonId: string) => {
    completeLesson(lessonId);
    updateStreak();
    showToast('+25 XP! Lesson complete.', 'success');
  };

  const isLessonUnlocked = (lessonIndex: number) => {
    if (lessonIndex === 0) return true;
    return completedLessons.includes(lessons[lessonIndex - 1].id);
  };

  return (
    <div className="pb-24 pt-4 px-5 max-w-lg mx-auto animate-fade-in">
      {/* Streak Counter */}
      <div className="text-center mb-4">
        <div className="inline-flex items-center gap-2 mb-1">
          <span className="font-sans text-xl text-ink" style={{ fontWeight: 500 }}>
            {streakDays > 0 ? `\uD83D\uDD25 Learning streak: Day ${streakDays}` : '\uD83D\uDD25 Start your streak'}
          </span>
        </div>
        <p className="text-xs text-ink-faint mb-2" style={{ fontWeight: 300 }}>
          {streakDays > 0
            ? 'Complete a lesson each day to keep your streak'
            : 'Start your learning streak \u2014 just 2 minutes a day'}
        </p>
        <div className="flex justify-center gap-1">
          {streakDots.map((dot, i) => (
            <div
              key={i}
              className={`w-3 h-3 rounded-full transition-all ${
                dot.active ? 'bg-success' : 'bg-surface-border'
              }`}
              title={dot.date}
            />
          ))}
        </div>
      </div>

      {/* Context Selector */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-1 -mx-1 px-1">
        {displayContexts.map(c => (
          <button
            key={c.id}
            onClick={() => {
              setSelectedContext(c.id);
              setExpandedLesson(null);
            }}
            className={`shrink-0 px-4 py-2 rounded-full text-xs font-sans font-medium transition-all duration-200 ${
              selectedContext === c.id
                ? 'bg-rose text-white shadow-warm'
                : 'bg-surface border border-surface-border text-ink-muted hover:border-ink/10'
            }`}
          >
            {c.emoji} {c.name}
          </button>
        ))}
      </div>

      <h1 className="font-sans text-xl text-ink mb-1" style={{ fontWeight: 600 }}>
        Improve your next photo
      </h1>
      <p className="text-sm text-ink-faint font-sans mb-5" style={{ fontWeight: 300 }}>
        Use short lessons and examples to understand what stronger photos have in common.
      </p>

      {/* Lesson List */}
      <div className="space-y-3">
        {lessons.map((lesson, index) => {
          const isCompleted = completedLessons.includes(lesson.id);
          const unlocked = isLessonUnlocked(index);
          const isExpanded = effectiveExpanded === lesson.id && unlocked;

          return (
            <div
              key={lesson.id}
              className={`card overflow-hidden transition-all duration-300 ${
                !unlocked ? 'opacity-50' : ''
              } ${isExpanded ? 'border-rose/20' : ''}`}
            >
              <button
                onClick={() => unlocked && setExpandedLesson(isExpanded ? null : lesson.id)}
                className="w-full text-left flex items-center gap-3 p-0"
                disabled={!unlocked}
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-sm">
                  {isCompleted ? '✅' : !unlocked ? '🔒' : (
                    <span className="w-8 h-8 bg-rose-faint text-rose rounded-full flex items-center justify-center font-sans font-medium text-xs">
                      {index + 1}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-sans font-medium text-ink truncate">
                    {lesson.title}
                  </div>
                  <div className="text-[11px] text-ink-faint truncate" style={{ fontWeight: 300 }}>
                    {lesson.subtitle}
                  </div>
                </div>
                {isCompleted && (
                  <div className="text-[11px] text-ink-faint shrink-0" style={{ fontWeight: 300 }}>
                    {lesson.beforeScore} → {lesson.afterScore}
                  </div>
                )}
              </button>

              {isExpanded && (
                <div className="mt-4 pt-4 border-t border-surface-border animate-fade-in">
                  <p className="text-sm font-sans text-ink mb-4" style={{ fontWeight: 300, lineHeight: '1.6' }}>
                    {lesson.content}
                  </p>

                  {/* Score comparison */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-1 text-center bg-cream-warm rounded-lg py-2">
                      <div className="text-[10px] text-ink-faint" style={{ fontWeight: 300 }}>Before</div>
                      <div className="font-sans text-lg text-ink-muted" style={{ fontWeight: 500 }}>
                        {lesson.beforeScore}
                      </div>
                    </div>
                    <div className="text-ink-faint">→</div>
                    <div className="flex-1 text-center bg-rose-faint rounded-lg py-2">
                      <div className="text-[10px] text-rose" style={{ fontWeight: 300 }}>After</div>
                      <div className="font-sans text-lg text-rose" style={{ fontWeight: 500 }}>
                        {lesson.afterScore}
                      </div>
                    </div>
                  </div>

                  {/* Tip */}
                  <div className="bg-cream-warm rounded-xl p-4 mb-4">
                    <div className="text-xs font-sans font-medium text-ink-muted mb-1">
                      Tip
                    </div>
                    <p className="text-xs font-sans text-ink" style={{ fontWeight: 400, lineHeight: '1.5' }}>
                      {lesson.tip}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    {!isCompleted && (
                      <button
                        onClick={() => handleComplete(lesson.id)}
                        className="btn-rose text-xs flex-1"
                      >
                        Mark as Complete ✓
                      </button>
                    )}
                    <button
                      onClick={() =>
                        navigate(
                          `/app?tab=new&vertical=${getProfileVerticalFromAppContext(selectedContext) || 'dating'}`
                        )
                      }
                      className={`${isCompleted ? 'btn-rose' : 'btn-secondary'} text-xs flex-1`}
                    >
                      Take a photo applying this →
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* More resources */}
      <div className="mt-8 pt-6 border-t border-surface-border">
        <div className="text-xs font-sans text-ink-faint tracking-wider uppercase mb-3">More resources</div>
        <div className="space-y-2">
          {selectedContext === 'dating' ? (
            <>
              <Link to="/guides/hinge" className="block text-sm text-accent hover:text-accent-strong">How to choose the best Hinge photos →</Link>
              <Link to="/guides/tinder" className="block text-sm text-accent hover:text-accent-strong">How to choose the best Tinder photos →</Link>
              <Link to="/guides/bumble" className="block text-sm text-accent hover:text-accent-strong">How to choose the best Bumble photos →</Link>
            </>
          ) : (
            <Link to="/guides/linkedin" className="block text-sm text-accent hover:text-accent-strong">How to choose the best LinkedIn headshot →</Link>
          )}
        </div>
      </div>
    </div>
  );
}
